'use client';
/** A board: sortable containers (columns) side by side, each with sortable items (cards) that also move between
 *  containers. One `DndContext` serves both kinds; `Sortable.tsx` owns the item seam and the wording rules.
 *
 *  Gestures differ from a flat list on purpose. A mouse picks a card up from anywhere on it after a short
 *  travel, so a click still opens it. A finger has to use the handle and hold it, because a long press on the
 *  card itself is the card's menu (`ContextMenuTrigger`). The keyboard starts from the handle.
 *
 *  While an item travels, the board keeps an in-flight arrangement in which the item already sits in the
 *  container it is over (moved on dnd-kit's `onDragOver`), hands it to the caller to draw, and paints the item
 *  itself in a `DragOverlay`, because its source element unmounts when it changes container. The drop is
 *  reported once; the caller applies it optimistically, so nothing snaps back while it saves. */
import { createContext, useContext, useEffect, useRef, useState, type MouseEventHandler, type ReactNode } from 'react';
import {
  DndContext, DragOverlay, KeyboardCode, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, closestCorners, getFirstCollision,
  pointerWithin, rectIntersection, useSensor, useSensors,
  type Announcements, type CollisionDetection, type DataRef, type DragEndEvent, type DragOverEvent, type DragStartEvent,
  type KeyboardCoordinateGetter, type UniqueIdentifier,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { HOLD_ACTIVATION, SORTABLE_DRIFT_PX, itemState, listenerOf, say, type SortableAnnouncements, type SortableId, type SortableItemState } from './Sortable';

export interface SortableBoardContainer<Id extends SortableId> {
  id: Id;
  /** The items on screen, in order. */
  itemIds: readonly Id[];
  /** The container's whole order, when a filter leaves only some items on screen. Positions are counted and
   *  moves reported in it; a dropped item lands right after the item on screen before it. */
  order?: readonly Id[];
}

export interface SortableBoardLabels {
  instructions: string;
  /** Positions among the containers. */
  container: SortableAnnouncements;
  /** Positions within `{container}`. */
  item: SortableAnnouncements;
}

export interface SortableBoardItemMove<Id extends SortableId> {
  itemId: Id;
  fromContainerId: Id;
  toContainerId: Id;
  /** 0-based index of the item in `itemIds`. */
  index: number;
  /** The target container's whole order after the drop, the item included. */
  itemIds: Id[];
}

export interface SortableBoardActive<Id extends SortableId> {
  kind: 'container' | 'item';
  id: Id;
}

export interface SortableBoardProps<Id extends SortableId> {
  /** The committed arrangement, containers in order. */
  containers: readonly SortableBoardContainer<Id>[];
  containerName: (id: Id) => string;
  itemName: (id: Id) => string;
  labels: SortableBoardLabels;
  /** A container dropped on another takes that container's place. */
  onContainerMove: (activeId: Id, overId: Id) => void;
  /** Called only when the item's container or index changed. Apply optimistically, then call onSettled
   *  after the write succeeds or rolls back. The preview bridges asynchronous cache updates. */
  onItemMove: (move: SortableBoardItemMove<Id>, onSettled: () => void) => void;
  renderOverlay: (active: SortableBoardActive<Id>) => ReactNode;
  /** Draws the arrangement: the committed one, or the in-flight one while an item travels. */
  children: (containers: readonly SortableBoardContainer<Id>[]) => ReactNode;
}

export interface SortableBoardItemState extends SortableItemState {
  /** Spread on the surface a mouse drags from (a card, a column header). */
  itemProps: { onMouseDown?: MouseEventHandler<HTMLElement> };
}

type Arrangement = readonly SortableBoardContainer<SortableId>[];
type Tag = SortableBoardActive<SortableId>;

/** dnd-kit has one id space; a container and an item may share an id, so each kind gets its own prefix. */
const keyOf = (kind: Tag['kind'], id: SortableId): string => `${kind}:${String(id)}`;
function tagOf(data: DataRef): Tag {
  const tag = data.current?.board as Tag | undefined;
  if (!tag) throw new Error('a sortable board droppable carries no board tag');
  return tag;
}
function containerOf(arrangement: Arrangement, itemId: SortableId): SortableBoardContainer<SortableId> {
  const container = arrangement.find((candidate) => candidate.itemIds.includes(itemId));
  if (!container) throw new Error(`sortable board item ${String(itemId)} is in no container`);
  return container;
}

function indexIn(order: readonly SortableId[], id: SortableId): number {
  const index = order.indexOf(id);
  if (index < 0) throw new Error(`sortable board item ${String(id)} is on screen but not in its container's order`);
  return index;
}

/** Where a carried item lands if it is dropped now: its container in the arrangement, at the over item's slot
 *  on screen, counted in the container's whole order. */
function projectedDrop(arrangement: Arrangement, itemId: SortableId, over: Tag | null) {
  const container = containerOf(arrangement, itemId);
  const from = container.itemIds.indexOf(itemId);
  const to = over?.kind === 'item' && container.itemIds.includes(over.id) ? container.itemIds.indexOf(over.id) : from;
  const order = container.order ?? container.itemIds;
  // Still on its own slot in the container it started in: it keeps its place, between hidden items included.
  // (An item that left and came back to another slot is placed by its neighbours below.)
  const ownSlot = order.slice(0, order.indexOf(itemId)).filter((id) => container.itemIds.includes(id)).length;
  if (to === from && order.includes(itemId) && ownSlot === from) return { containerId: container.id, index: order.indexOf(itemId), itemIds: [...order] };
  const onScreen = arrayMove([...container.itemIds], from, to);
  // Otherwise it goes right after its neighbour on screen; first on screen, right before the next one.
  const rest = order.filter((id) => id !== itemId);
  const before = onScreen[to - 1];
  const after = onScreen[to + 1];
  const index = before !== undefined ? indexIn(rest, before) + 1 : after !== undefined ? indexIn(rest, after) : rest.length;
  return { containerId: container.id, index, itemIds: [...rest.slice(0, index), itemId, ...rest.slice(index)] };
}

const BoardContext = createContext<Arrangement | null>(null);
function useArrangement(): Arrangement {
  const arrangement = useContext(BoardContext);
  if (!arrangement) throw new Error('sortable board parts must render inside SortableBoard');
  return arrangement;
}

const ARROWS: string[] = [KeyboardCode.Down, KeyboardCode.Right, KeyboardCode.Up, KeyboardCode.Left];

export function SortableBoard<Id extends SortableId>({ containers, containerName, itemName, labels, onContainerMove, onItemMove, renderOverlay, children }: SortableBoardProps<Id>) {
  const [active, setActive] = useState<Tag | null>(null);
  const [preview, setPreview] = useState<Arrangement | null>(null);
  // The latest arrangement and drag facts, read synchronously by collision detection, the keyboard and the
  // announcements, which dnd-kit calls between renders.
  const arrangementRef = useRef<Arrangement>(containers);
  const activeRef = useRef<Tag | null>(null);
  const startRef = useRef<ReturnType<typeof projectedDrop> | null>(null);
  const lastOverRef = useRef<UniqueIdentifier | null>(null);
  const changedContainerRef = useRef(false);
  const dropRef = useRef<ReturnType<typeof projectedDrop> | null>(null);
  const spokenRef = useRef('');
  const cancelledAnnouncementRef = useRef('');
  const invalidatedRef = useRef(false);
  const itemIds = new Set(containers.flatMap(container => [...container.itemIds]));
  const activeMissing = active !== null && (active.kind === 'item'
    ? !itemIds.has(active.id as Id) : !containers.some(container => container.id === active.id));
  const previewMissing = preview !== null && preview.some(container =>
    !containers.some(current => current.id === container.id) || container.itemIds.some(id => !itemIds.has(id as Id)));
  const invalidated = activeMissing || previewMissing;
  // Do not call either renderer with IDs that the current snapshot no longer owns. The native
  // cancelDrop path cancels the sensor's eventual release too, rather than persisting its old target.
  if (invalidated) invalidatedRef.current = true;
  const arrangement = invalidated ? containers : preview ?? containers;
  useEffect(() => {
    if (!invalidated) return;
    activeRef.current = null;
    arrangementRef.current = containers;
    lastOverRef.current = null;
    setActive(null);
    setPreview(null);
  }, [invalidated, containers]);

  // A container change re-lays the board out, during which dnd-kit can find nothing under the item; for that
  // frame it stays over itself instead of the slot it just left.
  useEffect(() => {
    const frame = requestAnimationFrame(() => { changedContainerRef.current = false; });
    return () => cancelAnimationFrame(frame);
  }, [preview]);

  const collisionDetection: CollisionDetection = (args) => {
    if (invalidatedRef.current) return [];
    const kind = tagOf(args.active.data).kind;
    const containersOnly = args.droppableContainers.filter((entry) => tagOf(entry.data).kind === 'container');
    if (kind === 'container') return closestCenter({ ...args, droppableContainers: containersOnly });
    const underPointer = pointerWithin(args);
    let overId = getFirstCollision(underPointer.length > 0 ? underPointer : rectIntersection(args), 'id');
    if (overId != null) {
      const over = tagOf(args.droppableContainers.find((entry) => entry.id === overId)!.data);
      if (over.kind === 'container') {
        // Over a container with items, the target is its closest item; an empty one is the target itself.
        const itemIds = arrangementRef.current.find((container) => container.id === over.id)!.itemIds;
        if (itemIds.length > 0) {
          const keys = new Set(itemIds.map((id) => keyOf('item', id)));
          overId = closestCenter({ ...args, droppableContainers: args.droppableContainers.filter((entry) => keys.has(String(entry.id))) })[0]?.id ?? overId;
        }
      }
      lastOverRef.current = overId;
      return [{ id: overId }];
    }
    if (changedContainerRef.current) lastOverRef.current = args.active.id;
    return lastOverRef.current == null ? [] : [{ id: lastOverRef.current }];
  };

  // Up and Down walk an item within its container, Left and Right take it to the nearest item (or empty
  // container) beside it; a container moves Left and Right among containers.
  const coordinateGetter: KeyboardCoordinateGetter = (event, { context: { active: activeEntry, collisionRect, droppableRects, droppableContainers, over } }) => {
    if (!ARROWS.includes(event.code)) return undefined;
    event.preventDefault();
    const carried = activeRef.current;
    if (!carried || !activeEntry || !collisionRect) return undefined;
    const sideways = event.code === KeyboardCode.Left || event.code === KeyboardCode.Right;
    const current = arrangementRef.current;
    const home = carried.kind === 'item' ? containerOf(current, carried.id).id : null;
    const candidates = droppableContainers.getEnabled().filter((entry) => {
      if (entry.id === activeEntry.id) return false;
      const rect = droppableRects.get(entry.id);
      if (!rect) return false;
      const target = tagOf(entry.data);
      if (carried.kind === 'container') {
        if (target.kind !== 'container' || !sideways) return false;
      } else if (target.kind === 'container') {
        if (!sideways || current.find((container) => container.id === target.id)!.itemIds.length > 0) return false;
      } else if ((containerOf(current, target.id).id === home) === sideways) {
        return false;
      }
      switch (event.code) {
        case KeyboardCode.Down: return collisionRect.top < rect.top;
        case KeyboardCode.Up: return collisionRect.top > rect.top;
        case KeyboardCode.Left: return collisionRect.left > rect.left;
        default: return collisionRect.left < rect.left;
      }
    });
    const collisions = closestCorners({ active: activeEntry, collisionRect, droppableRects, droppableContainers: candidates, pointerCoordinates: null });
    let targetId = getFirstCollision(collisions, 'id');
    if (targetId === over?.id && collisions.length > 1) targetId = collisions[1]!.id;
    const rect = targetId == null ? undefined : droppableRects.get(targetId);
    if (!rect) return undefined;
    // Moving down past a shorter item lines the bottoms up, so the carried item lands on that slot.
    return { x: rect.left, y: event.code === KeyboardCode.Down ? rect.top + rect.height - collisionRect.height : rect.top };
  };

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: SORTABLE_DRIFT_PX } }),
    useSensor(TouchSensor, HOLD_ACTIVATION),
    useSensor(KeyboardSensor, { coordinateGetter }),
  );

  const containerPosition = (id: SortableId) => arrangementRef.current.findIndex((container) => container.id === id);
  const speakContainer = (template: string, id: SortableId, at: SortableId) => say(template, {
    name: containerName(id as Id), position: containerPosition(at) + 1, total: arrangementRef.current.length,
  });
  const speakItem = (template: string, id: SortableId, drop: ReturnType<typeof projectedDrop>) => say(template, {
    name: itemName(id as Id), position: drop.index + 1, total: drop.itemIds.length, container: containerName(drop.containerId as Id),
  });
  const announcements: Announcements = {
    onDragStart: ({ active: started }) => {
      const tag = tagOf(started.data);
      if (tag.kind === 'container') {
        spokenRef.current = keyOf('container', tag.id);
        return speakContainer(labels.container.pickedUp, tag.id, tag.id);
      }
      const start = startRef.current!;
      spokenRef.current = `${String(start.containerId)}:${start.index}`;
      return speakItem(labels.item.pickedUp, tag.id, start);
    },
    onDragOver: ({ active: moving, over }) => {
      if (invalidatedRef.current) return undefined;
      const tag = tagOf(moving.data);
      const target = over ? tagOf(over.data) : null;
      if (tag.kind === 'container') {
        const at = target?.id ?? tag.id;
        if (keyOf('container', at) === spokenRef.current) return undefined;
        spokenRef.current = keyOf('container', at);
        return speakContainer(labels.container.moved, tag.id, at);
      }
      const drop = projectedDrop(arrangementRef.current, tag.id, target);
      const spoken = `${String(drop.containerId)}:${drop.index}`;
      if (spoken === spokenRef.current) return undefined;
      spokenRef.current = spoken;
      return speakItem(labels.item.moved, tag.id, drop);
    },
    onDragEnd: ({ active: ended, over }) => {
      const tag = tagOf(ended.data);
      if (tag.kind === 'container') return speakContainer(labels.container.dropped, tag.id, over ? tagOf(over.data).id : tag.id);
      return speakItem(labels.item.dropped, tag.id, dropRef.current!);
    },
    onDragCancel: ({ active: cancelled }) => {
      if (invalidatedRef.current) return cancelledAnnouncementRef.current;
      const tag = tagOf(cancelled.data);
      if (tag.kind === 'container') return speakContainer(labels.container.cancelled, tag.id, tag.id);
      return speakItem(labels.item.cancelled, tag.id, startRef.current!);
    },
  };

  const settle = (keepPreview = false) => {
    activeRef.current = null;
    lastOverRef.current = null;
    arrangementRef.current = containers;
    setActive(null);
    if (!keepPreview) setPreview(null);
  };

  const onDragStart = ({ active: started }: DragStartEvent) => {
    invalidatedRef.current = false;
    const tag = tagOf(started.data);
    activeRef.current = tag;
    arrangementRef.current = containers;
    if (tag.kind === 'item') startRef.current = projectedDrop(containers, tag.id, null);
    cancelledAnnouncementRef.current = tag.kind === 'item'
      ? speakItem(labels.item.cancelled, tag.id, startRef.current!)
      : speakContainer(labels.container.cancelled, tag.id, tag.id);
    setActive(tag);
  };

  const onDragOver = ({ active: moving, over }: DragOverEvent) => {
    if (invalidatedRef.current) return;
    const tag = tagOf(moving.data);
    if (tag.kind !== 'item' || !over) return;
    const target = tagOf(over.data);
    const current = arrangementRef.current;
    const from = containerOf(current, tag.id);
    const to = target.kind === 'container' ? current.find((container) => container.id === target.id)! : containerOf(current, target.id);
    if (from.id === to.id) return;
    // Entering a container over one of its items, the item goes before it, or after it once the carried
    // item's top has passed that item's bottom; over the container itself it goes last.
    const overIndex = target.kind === 'item' ? to.itemIds.indexOf(target.id) : to.itemIds.length;
    const translated = moving.rect.current.translated;
    const below = target.kind === 'item' && translated !== null && translated.top > over.rect.top + over.rect.height;
    const index = overIndex + (below ? 1 : 0);
    const next = current.map((container) => {
      if (container.id === from.id) return { ...container, itemIds: container.itemIds.filter((id) => id !== tag.id) };
      if (container.id === to.id) return { ...container, itemIds: [...container.itemIds.slice(0, index), tag.id, ...container.itemIds.slice(index)] };
      return container;
    });
    arrangementRef.current = next;
    changedContainerRef.current = true;
    setPreview(next);
  };

  const onDragEnd = ({ active: ended, over }: DragEndEvent) => {
    const tag = tagOf(ended.data);
    if (tag.kind === 'container') {
      const target = over ? tagOf(over.data) : null;
      if (target && target.id !== tag.id) onContainerMove(tag.id as Id, target.id as Id);
    } else {
      const drop = projectedDrop(arrangementRef.current, tag.id, over ? tagOf(over.data) : null);
      dropRef.current = drop;
      const start = startRef.current!;
      if (drop.containerId !== start.containerId || drop.index !== start.index) {
        const droppedPreview = preview;
        settle(true);
        onItemMove({ itemId: tag.id as Id, fromContainerId: start.containerId as Id, toContainerId: drop.containerId as Id, index: drop.index, itemIds: drop.itemIds as Id[] },
          () => setPreview(current => current === droppedPreview ? null : current));
        return;
      }
    }
    settle();
  };

  return (
    <BoardContext.Provider value={arrangement}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={() => settle()}
        cancelDrop={() => invalidatedRef.current}
        accessibility={{ announcements, screenReaderInstructions: { draggable: labels.instructions } }}
      >
        <SortableContext items={arrangement.map((container) => keyOf('container', container.id))} strategy={horizontalListSortingStrategy}>
          {children(arrangement as readonly SortableBoardContainer<Id>[])}
        </SortableContext>
        {/* No drop animation: the source keeps its slot, so the item is already where it lands. */}
        <DragOverlay dropAnimation={null}>{active && !invalidated ? renderOverlay(active as SortableBoardActive<Id>) : null}</DragOverlay>
      </DndContext>
    </BoardContext.Provider>
  );
}

/** One container's items, in the order of the arrangement being drawn. */
export function SortableBoardItems({ containerId, children }: { containerId: SortableId; children: ReactNode }) {
  const container = useArrangement().find((candidate) => candidate.id === containerId);
  if (!container) throw new Error(`sortable board container ${String(containerId)} is not on the board`);
  return (
    <SortableContext id={keyOf('container', containerId)} items={container.itemIds.map((id) => keyOf('item', id))} strategy={verticalListSortingStrategy}>
      {children}
    </SortableContext>
  );
}

function boardState(sortable: ReturnType<typeof useSortable>): SortableBoardItemState {
  return {
    ...itemState(sortable, ['onTouchStart', 'onKeyDown']),
    itemProps: { onMouseDown: listenerOf<MouseEventHandler<HTMLElement>>(sortable.listeners, 'onMouseDown') },
  };
}

/** One container. Its node is also the drop target while it holds no item; `disabled` stops it being dragged,
 *  never from receiving items. */
export function useSortableBoardContainer(id: SortableId, options: { disabled?: boolean } = {}): SortableBoardItemState {
  useArrangement();
  return boardState(useSortable({
    id: keyOf('container', id),
    data: { board: { kind: 'container', id } satisfies Tag },
    disabled: { draggable: options.disabled === true, droppable: false },
  }));
}

/** One item, rendered inside its container's `SortableBoardItems`. */
export function useSortableBoardItem(id: SortableId): SortableBoardItemState {
  useArrangement();
  return boardState(useSortable({ id: keyOf('item', id), data: { board: { kind: 'item', id } satisfies Tag } }));
}
